import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../../api/client";
import Logo from "../../components/Logo";
import ThemeToggle from "../../components/ThemeToggle";
import useAuthStore from "../../store/authStore";

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextUrl = searchParams.get("next");
  const setSeekerAuth = useAuthStore((s) => s.setSeekerAuth);
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post("/auth/login", form);
      setSeekerAuth(data.access_token, data.user);
      navigate(nextUrl || "/app/dashboard");
    } catch (err) {
      const msg = err.response?.data?.detail ?? err.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-light-bg dark:bg-brand-dark-bg flex flex-col">
      {/* Nav */}
      <nav className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-white/10">
        <Logo className="h-12" />
        <ThemeToggle />
      </nav>

      {/* Card */}
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="bg-white dark:bg-brand-dark-surface rounded-2xl shadow-sm border border-gray-100 dark:border-white/10 p-8 w-full max-w-sm">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
            Welcome back
          </h1>
          <p className="text-gray-400 text-sm mb-6">Sign in to your account</p>

          {error && (
            <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3">
              <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
              {error.toLowerCase().includes("verify") && (
                <button
                  onClick={async () => {
                    try {
                      await api.post(
                        `/auth/resend-verification?email=${encodeURIComponent(form.email)}`,
                      );
                      alert("Verification email resent — check your inbox.");
                    } catch {
                      alert("Could not resend — try again later.");
                    }
                  }}
                  className="text-brand-violet dark:text-brand-violet-on-dark text-xs hover:underline mt-2 block"
                >
                  Resend verification email →
                </button>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <input
              name="email"
              type="email"
              placeholder="Email"
              required
              value={form.email}
              onChange={handleChange}
              className="border border-gray-200 dark:border-white/10 bg-white dark:bg-brand-dark-bg rounded-lg px-4 py-2.5 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-violet dark:focus:ring-brand-violet-on-dark"
            />
            <input
              name="password"
              type="password"
              placeholder="Password"
              required
              value={form.password}
              onChange={handleChange}
              className="border border-gray-200 dark:border-white/10 bg-white dark:bg-brand-dark-bg rounded-lg px-4 py-2.5 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-violet dark:focus:ring-brand-violet-on-dark"
            />
            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                className="text-xs text-brand-violet dark:text-brand-violet-on-dark hover:underline"
              >
                Forgot password?
              </Link>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="bg-brand-violet text-white py-2.5 rounded-lg font-medium hover:opacity-90 disabled:opacity-50 transition"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <p className="text-center text-sm text-gray-400 mt-6">
            No account?{" "}
            <Link
              to={
                nextUrl
                  ? `/register?next=${encodeURIComponent(nextUrl)}`
                  : "/register"
              }
              className="text-brand-violet dark:text-brand-violet-on-dark font-medium hover:underline"
            >
              Register
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
